import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import axios from "axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";


export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}


export const fetchUserTypeList = createAsyncThunk(
  "userType/fetchUserTypeList",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search?.trim() || "";

      // Build the ordering param for the API
      if (sort) {
        params.ordering = order === "desc" ? `-${sort}` : sort;
      }

      //Add each filter as a query param (e.g. organization_name=...)
      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          const { id, value } = filter;
          if (!id || value === undefined || value === "") return;
          if (
            (id === "updated_at" || id === "created_at") &&
            typeof value === "object"
          ) {
            const { from_date, to_date } = value as {
              from_date?: string;
              to_date?: string;
            };
            if (from_date) params[`${id}_from`] = from_date;
            if (to_date) params[`${id}_to`] = to_date;
          } else {
            params[id] = value;
          }
        });
      }
      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          const { id, value } = filter;
          if (!id || value === undefined || value === "") return;
          if (
            (id === "updated_at" || id === "created_at") &&
            typeof value === "object"
          ) {
            const { from_date, to_date } = value as {
              from_date?: string;
              to_date?: string;
            };
            if (id === "updated_at") {
              if (from_date) params["updated_at_from"] = from_date;
              if (to_date) params["updated_at_to"] = to_date;
            }
            if (id === "created_at") {
              if (from_date) params["created_at_from"] = from_date;
              if (to_date) params["created_at_to"] = to_date;
            }
          } else {
            params[id] = value;
          }
        });
      }

      const response: any = await Axios.get(
        `${API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.LIST}`,
        { params }
      );

      const responseData = response.data;
      return {
        data: responseData?.data || responseData?.results || [],
        rowCount: responseData?.total_records || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchUserTypeList error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch User Types");
    }
  }
);

export const fetchUserTypeById = createAsyncThunk(
  "userType/fetchUserTypeById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.DETAIL(id)}`
      );
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 
          "Failed to fetch User Type by ID"
      );
    }
  }
);

export const updateUserType = createAsyncThunk(
  "userType/updateUserType",
  async ({ id, data }:{ id:string, data:any }, { rejectWithValue }) => {
    try {
      const response = await Axios.put(
        API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.UPDATE(id),
        data
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data ||
          "Failed to update User Type"
      );
    }
  }
);

export const updateUserTypeStatus = createAsyncThunk<
  any, // return type
  string, // argument type (id)
  { rejectValue: string } // rejected value type
>("userType/updateUserTypeStatus", async (id, { rejectWithValue }) => {
  try {
    const response = await Axios.patch(
      API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.STATUS_UPDATE(id)
    );

    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data ||
        "Failed to update User Type status"
    );
  }
});

export const addUserType = createAsyncThunk<
  any, // return type
  any, // argument type
  { rejectValue: string } // reject value type
>("userType/addUserType", async (data:any, { rejectWithValue }) => {
  try {
    const response = await Axios.post(
      API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.CREATE,
      data
    );

    return response?.data?.data; // return only useful payload
  } catch (error: any) {
    console.log("error thunk", error.response?.data);
    return rejectWithValue(
      error?.response?.data ||
        "Failed to create User Type"
    );
  }
});

export const deleteUserType = createAsyncThunk(
  "userType/deleteUserType",
  async (id: string, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_ENDPOINT?.USER_MANAGEMENT?.ORGANIZATION?.DELETE(id)}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to Delete User Type"
      );
    }
  }
);

export const fetchUserTypeDropdown = createAsyncThunk(
  "userType/fetchUserTypeDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`${API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.DROPDOWN}`);

      return response?.data?.data ?? [];
    } catch (error: any) {
      console.error("fetchOrganizationDropdown error:", error);
      return rejectWithValue(
        error.response?.data ||
          "Failed to fetch organization dropdown"
      );
    }
  }
);

export const UserTypeExportsCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();
  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(({ id, value }: { id: string; value: any }) => {
      if (id && value !== undefined && value !== null) {
        if (typeof value === "object" && value !== null) {
          const maybeDateFilter = value as {
            from_date?: string;
            to_date?: string;
          };

          if (
            (maybeDateFilter.from_date || maybeDateFilter.to_date) &&
            (id === "created_at" || id === "updated_at")
          ) {
            if (maybeDateFilter.from_date) {
              params.append(`${id}_from`, maybeDateFilter.from_date);
            }
            if (maybeDateFilter.to_date) {
              params.append(`${id}_to`, maybeDateFilter.to_date);
            }
            return;
          }

          // Other object filters
          Object.entries(value).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== "") {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(id, value);
        }
      }
    });
  }

  const response = await Axios.get(
    `${
      API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.EXPORT
    }?${params.toString()}`,
    {
      responseType: "blob",
    }
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "User_Types"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

